import "../../kendo.icons.js";
import { htmlService } from "../../core/services/html.service";
import { dateParserService } from "../../core/services/date-parser.service";
import { formatterService } from "../../core/services/formatter.service";
import { dateUtilsService } from "../../core/services/date-utils.service";
import { utilsService } from "../../core/services/utils.service";
import { STYLES, ICONS, REFERENCES, MESSAGE_STATUS, FILES_LAYOUT_MODE } from "../constants";
import type { IMessage, IAuthor, IChatMessages, IChatAttachment, TimestampTemplateFunction, MessageTemplateFunction, MessageStatusTemplateFunction, MessageContentTemplateFunction, AttachmentTemplateFunction, UserStatusTemplateFunction, MessageStatus, IMessageSettings, IMessageStatusSettings, FilesLayoutMode, AttachmentLayoutMode, TimestampVisibility, FilesTemplateFunction } from "../models";
import { renderAvatar, renderTypingIndicator } from "./common.template";
import { renderFiles, renderMessageReference } from "./file.template";

declare const kendo: any;

/**
 * Renders the retry button for failed messages.
 */
export const renderRetryButton = (
    message: IMessage,
    retryText: string
): string => {
    if (!message.failed || !message.isOwnMessage) {
        return "";
    }
    return kendo.html.renderButton(
        `<button class="${STYLES.messageRetryButton}" ${REFERENCES.retryButton} title="${retryText}" aria-label="${retryText}" data-uid="${message.uid || ""}"></button>`,
        { icon: ICONS.retry, size: "small", fillMode: "clear" }
    );
};

export const renderFailedContent = (
    failedText: string
): string => {
    return `<span class="${STYLES.messageFailedContent}">
        ${kendo.ui.icon({ icon: ICONS.warningTriangle, size: "xsmall" })}
        <span class="${STYLES.messageFailedText}">${htmlService.encode(failedText)}</span>
    </span>`;
};

/**
 * Renders the message status indicator.
 * Supports custom status settings with icon, text, and cssClass for enhanced display.
 */
export const renderMessageStatus = (
    status: MessageStatus | undefined,
    message: IMessage,
    statusTemplate?: MessageStatusTemplateFunction | null,
    statusSettings?: Record<MessageStatus, IMessageStatusSettings>
): string => {
    if (!status) {
        return "";
    }

    if (statusTemplate) {
        return statusTemplate({ status, message });
    }

    const settings = statusSettings?.[status];
    const statusClass = `${STYLES.messageStatus}${settings?.cssClass ? " " + settings.cssClass : ""}`;

    let iconHtml = "";
    if (settings?.icon) {
        iconHtml = kendo.ui.icon({ icon: settings.icon });
    }

    const statusText = settings?.text !== undefined
        ? settings.text
        : status.charAt(0).toUpperCase() + status.slice(1);

    return `<span class="${statusClass}">${iconHtml}${htmlService.encode(statusText)}</span>`;
};

/**
 * Renders a single attachment card.
 */
export const renderAttachment = (
    attachment: IChatAttachment,
    message: IMessage,
    attachmentIndex: number,
    attachmentTemplate?: AttachmentTemplateFunction | null
): string => {
    if (attachmentTemplate) {
        return attachmentTemplate({ attachment, message });
    }

    const title = attachment.title ? `<div class="${STYLES.cardTitle}">${htmlService.encode(attachment.title)}</div>` : "";
    const subtitle = attachment.subtitle ? `<div class="${STYLES.cardSubtitle}">${htmlService.encode(attachment.subtitle)}</div>` : "";
    const image = attachment.thumbnailUrl ? `<div class="${STYLES.cardMedia}"><img src="${htmlService.encode(attachment.thumbnailUrl)}" alt="" /></div>` : "";
    const actions = attachment.actions?.length
        ? `<div class="${STYLES.cardActions}">${attachment.actions.map((action, actionIndex) => {
            const label = action.title || action.text;

            return `<button class="${STYLES.cardAction} ${STYLES.button}" ${REFERENCES.attachmentActionButton} data-attachment-index="${attachmentIndex}" data-action-index="${actionIndex}">${htmlService.encode(label)}</button>`;
        }).join("")}</div>`
        : "";

    return `<div class="${STYLES.card}">
        ${image}
        <div class="${STYLES.cardBody}">
            ${title}
            ${subtitle}
        </div>
        ${actions}
    </div>`;
};

/**
 * Renders attachments with the specified layout.
 */
export const renderAttachments = (
    attachments: IChatAttachment[] | undefined,
    message: IMessage,
    layout: AttachmentLayoutMode = "list",
    attachmentTemplate?: AttachmentTemplateFunction | null
): string => {
    if (!attachments?.length) {
        return "";
    }

    const attachmentsHtml = attachments.map((attachment, attachmentIndex) => renderAttachment(attachment, message, attachmentIndex, attachmentTemplate)).join("");

    if (layout === "carousel") {
        return `<div class="${STYLES.cardDeckScrollWrap}">
            ${new kendo.ui.Button(`<button ${REFERENCES.leftScrollButton}>`, { icon: ICONS.chevronLeft }).wrapper[0].outerHTML}
            <div class="${STYLES.cardDeck}">${attachmentsHtml}</div>
            ${new kendo.ui.Button(`<button ${REFERENCES.rightScrollButton}>`, { icon: ICONS.chevronRight }).wrapper[0].outerHTML}
        </div>`;
    }

    return `<div class="${STYLES.cardList}">${attachmentsHtml}</div>`;
};

/**
 * Renders a single chat message.
 */
export const renderMessage = (
    message: IMessage & { isOwnMessage: boolean; author: IAuthor },
    replyMessage: IMessage | null,
    downloadAll: boolean,
    messages: IChatMessages,
    expandable: boolean,
    messageTimeFormat: string,
    skipSanitization: boolean,
    statusTemplate?: MessageStatusTemplateFunction | null,
    filesLayoutMode: FilesLayoutMode = FILES_LAYOUT_MODE.VERTICAL,
    filesTemplate?: FilesTemplateFunction | null,
    contentTemplate?: MessageContentTemplateFunction | null,
    attachmentTemplate?: AttachmentTemplateFunction | null,
    attachmentLayout?: AttachmentLayoutMode,
    timestampVisibility: TimestampVisibility = "onFocus",
    statusSettings?: Record<MessageStatus, IMessageStatusSettings> | null,
    showMessageTime: boolean = true
): string => {
    const isDeleted = message.isDeleted;
    const isFailedStatus = message.status === MESSAGE_STATUS.FAILED;
    const isFailed = message.isOwnMessage && (message.failed || isFailedStatus);
    const messageStatus = !message.isOwnMessage && isFailedStatus ? undefined : message.status;
    const expandableClasses = expandable && !message.isTyping
        ? [STYLES.bubbleExpandable, STYLES.expanded]
        : [];

    const replyMessageHtml = replyMessage
        ? renderMessageReference({
            text: replyMessage.text,
            files: replyMessage.files,
            isOwnMessage: replyMessage.isOwnMessage,
            isPinMessage: false,
            renderCloseButton: false,
            renderFileMenuButton: false
        })
        : "";

    let messageContent = "";
    if (message.isTyping && !message.isOwnMessage) {
        messageContent = renderTypingIndicator();
    } else if (contentTemplate && !isDeleted) {
        messageContent = contentTemplate(message);
    } else if (isDeleted) {
        messageContent = message.isOwnMessage
            ? htmlService.encode(messages.selfMessageDeleted)
            : htmlService.encode(messages.otherMessageDeleted);
    } else {
        messageContent = htmlService.convertTextUrlToLink(message.text || "", skipSanitization);
    }

    const timeAttributes = timestampVisibility === "onFocus"
        ? ` data-timestamp-visibility="${timestampVisibility}"`
        : "";
    const timeClasses = [
        STYLES.messageTime,
        timestampVisibility === "onFocus" ? STYLES.hidden : ""
    ].filter(Boolean).join(" ");
    const timeHtml = showMessageTime ? `<time class="${timeClasses}"${timeAttributes}>${formatterService.toString(dateParserService.parseDate(message.timestamp), messageTimeFormat)}</time>` : "";
    const statusHtml = renderMessageStatus(messageStatus, message, statusTemplate, statusSettings || null);
    const retryHtml = isFailed ? renderRetryButton(message, messages.retryMessage || "Retry") : "";
    const failedContentHtml = isFailed ? renderFailedContent((statusSettings?.[MESSAGE_STATUS.FAILED]?.text) || "Failed to send") : "";
    const messageInfoHtml = `<div class="${STYLES.messageInfo}">${statusHtml}${timeHtml}${failedContentHtml}</div>`;
    let filesHtml = "";

    if (message.files?.length && !isDeleted) {
        filesHtml = filesTemplate
            ? filesTemplate(message.files, downloadAll, messages, false, filesLayoutMode)
            : renderFiles(message.files, downloadAll, messages, false, filesLayoutMode);
    }

    return `<div class="${STYLES.message}${isDeleted ? " " + STYLES.messageRemoved : ""}${isFailed ? " " + STYLES.messageFailed : ""}" data-uid="${htmlService.encode(message.uid ?? "")}">
        ${retryHtml}
        <div class="${STYLES.chatBubble} ${STYLES.bubble} ${expandableClasses.join(" ")}">
            <div class="${STYLES.bubbleContent}">
                ${replyMessageHtml}
                <span class="${STYLES.chatBubbleText}">${messageContent}</span>
                ${filesHtml}
            </div>
            ${expandable && !message.isTyping ? `<span class="${STYLES.bubbleExpandableIndicator}" ${REFERENCES.bubbleExpandableIndicator}>${kendo.ui.icon({ icon: ICONS.chevronUp })}</span>` : ""}
        </div>
        ${messageInfoHtml}
        ${!isDeleted ? `<div class="${STYLES.messageToolbar}"></div>` : ""}
    </div>`;
};

/**
 * Renders a message group with author avatar and multiple messages.
 */
export const renderMessageGroup = (
    context: {
        message: IMessage;
        author: IAuthor;
        isOwnMessage: boolean;
        replyMessage?: IMessage | null;
        downloadAll?: boolean;
        messages?: IChatMessages;
        expandable?: boolean;
        fullWidth?: boolean;
        messageTimeFormat?: string;
        timestampTemplate?: TimestampTemplateFunction | null;
        statusTemplate?: MessageStatusTemplateFunction | null;
        showTimestamp?: boolean;
        messageTemplate?: MessageTemplateFunction;
        filesTemplate?: FilesTemplateFunction | null;
        skipSanitization?: boolean;
        messageSettings?: IMessageSettings;
        filesLayoutMode?: FilesLayoutMode;
        contentTemplate?: MessageContentTemplateFunction | null;
        attachmentTemplate?: AttachmentTemplateFunction | null;
        attachmentLayout?: AttachmentLayoutMode;
        userStatusTemplate?: UserStatusTemplateFunction | null;
        timestampVisibility?: TimestampVisibility;
        statusSettings?: Record<MessageStatus, IMessageStatusSettings> | null;
        showMessageTime?: boolean;
    }
): string => {
    const {
        message,
        author,
        isOwnMessage,
        replyMessage,
        downloadAll = true,
        messages,
        expandable = false,
        fullWidth = false,
        messageTimeFormat = "ddd MMM dd yyyy",
        timestampTemplate,
        statusTemplate,
        showTimestamp = false,
        messageTemplate,
        filesTemplate,
        skipSanitization = false,
        messageSettings,
        filesLayoutMode = FILES_LAYOUT_MODE.VERTICAL,
        contentTemplate,
        attachmentTemplate,
        attachmentLayout,
        userStatusTemplate,
        timestampVisibility = "onFocus",
        statusSettings,
        showMessageTime = true
    } = context;

    const effectiveShowAvatar = messageSettings?.showAvatar !== undefined
        ? messageSettings.showAvatar && author && author.imageUrl
        : author && author.imageUrl;
    const effectiveShowUsername = messageSettings?.showUsername !== undefined
        ? messageSettings.showUsername
        : true;
    const effectiveExpandable = messageSettings?.allowMessageCollapse !== undefined
        ? messageSettings.allowMessageCollapse
        : expandable;
    const effectiveFullWidth = messageSettings?.messageWidthMode === "full" || fullWidth;

    const groupClasses = [
        STYLES.messageGroup,
        isOwnMessage ? STYLES.messageGroupSender : STYLES.messageGroupReceiver,
        effectiveShowAvatar ? "" : STYLES.noAvatar,
        effectiveFullWidth ? STYLES.messageGroupFullWidth : ""
    ].filter(Boolean).join(" ");

    let timestampContent = "";
    if (showTimestamp && message.timestamp) {
        const messageDate = dateParserService.parseDate(message.timestamp);
        if (utilsService.isFunction(timestampTemplate)) {
            timestampContent = timestampTemplate!({ date: messageDate, message });
        } else {
            const relativeDateText = dateUtilsService.getRelativeDateString(messageDate);
            timestampContent = `<div class="${STYLES.timestamp}">${relativeDateText}</div>`;
        }
    }

    const userStatusHtml = effectiveShowAvatar && userStatusTemplate
        ? userStatusTemplate({ message: { ...message, author } })
        : "";

    const effectiveAttachmentLayout = message.attachmentLayout || attachmentLayout || "list";
    const attachmentsHtml = !message.isDeleted
        ? renderAttachments(message.attachments, message, effectiveAttachmentLayout, attachmentTemplate)
        : "";

    // Use custom messageTemplate or default renderMessage
    let messageHtml: string;
    if (messageTemplate) {
        messageHtml = messageTemplate(
            { ...message, isOwnMessage, author },
            replyMessage ?? null,
            downloadAll,
            messages!,
            effectiveExpandable,
            messageTimeFormat,
            skipSanitization,
            statusTemplate
        );
    } else {
        messageHtml = renderMessage(
            { ...message, isOwnMessage, author },
            replyMessage ?? null,
            downloadAll,
            messages!,
            effectiveExpandable,
            messageTimeFormat,
            skipSanitization,
            statusTemplate,
            filesLayoutMode,
            filesTemplate,
            contentTemplate,
            attachmentTemplate,
            attachmentLayout,
            timestampVisibility,
            statusSettings,
            showMessageTime
        );
    }

    return `${showTimestamp && timestampContent ? timestampContent : ""}
        <div class="${groupClasses}">${userStatusHtml ? `<div ${REFERENCES.userStatusWrapper}>${userStatusHtml}</div>` : ""}
            ${effectiveShowAvatar ? renderAvatar(author.imageUrl!, author.imageAltText) : ""}
            <div class="${STYLES.messageGroupContent}">
                ${effectiveShowUsername ? `<span class="${STYLES.messageAuthor}">${htmlService.encode(author.name ?? "")}</span>` : ""}
                ${messageHtml}
            </div>
        </div>
        ${attachmentsHtml}`;
};
